// Handles localStorage draft persistence for the inspection report form (text fields only).
// Photos are handled separately by useMediaDraft, which uses IndexedDB for binary blobs.
//
// Draft lifecycle:
//   1. On mount: checks localStorage for a saved draft. If found, restores form state
//      and sets dirty=true so the debounced save loop keeps it current.
//   2. While the form is open: any change sets dirty=true, triggering a debounced save.
//      The draft is only written when dirty=true — a pristine form never writes to storage.
//   3. On pagehide/beforeunload: flushes the draft synchronously to catch browser closes.
//   4. On submit (success): the page component calls clearDraft() explicitly.
//
// draftCheckComplete:
//   Set to true at the end of the init effect (both the "found" and "not found" paths).
//   useMediaDraft waits for this before running its own restore/cleanup logic, because
//   both hooks fire in the same React flush and hasDraft starts as false — without the
//   gate, useMediaDraft would delete blobs before this hook has a chance to set hasDraft=true.

import { useEffect, useRef, type Dispatch, type SetStateAction } from 'react';
import { loadDraft, saveDraft } from '../utils/inspectionReportDraftStorage';
import { initialFormValues, type InspectionFormValues } from '../utils/inspectionReportFormDefaults';
import { createDefaultHvacSystem } from '../components/HvacSystemFields';
import type { HvacSystemValues } from '../components/HvacSystemFields';
import { createDefaultPlumbingSystem } from '../components/PlumbingSystemFields';
import type { PlumbingSystemValues } from '../components/PlumbingSystemFields';

const PERSIST_DEBOUNCE_MS = 400;

function parsePrefill(snapshot: string): Partial<InspectionFormValues> {
  try {
    return JSON.parse(snapshot) as Partial<InspectionFormValues>;
  } catch {
    return {};
  }
}

interface UseInspectionReportDraftParams {
  homeInspectionJobId: string | undefined;
  /** Stable serialized prefill from `JSON.stringify(prefillData ?? {})` to avoid effect churn from new object identity. */
  prefillSnapshot: string;
  formValues: InspectionFormValues;
  hvacSystems: HvacSystemValues[];
  plumbingSystems: PlumbingSystemValues[];
  dirty: boolean;
  setFormValues: Dispatch<SetStateAction<InspectionFormValues>>;
  setHvacSystems: Dispatch<SetStateAction<HvacSystemValues[]>>;
  setPlumbingSystems: Dispatch<SetStateAction<PlumbingSystemValues[]>>;
  setDirty: Dispatch<SetStateAction<boolean>>;
  setRestoredFromDraft: Dispatch<SetStateAction<boolean>>;
  /** Signals useMediaDraft that the localStorage check is complete and hasDraft is trustworthy. */
  setDraftCheckComplete: Dispatch<SetStateAction<boolean>>;
  isPending: boolean;
}

/**
 * Local draft persistence (debounced), flush on pagehide, and navigation guard on beforeunload.
 * stateRef holds latest state so unload handlers do not close over stale values.
 */
export function useInspectionReportDraft({
  homeInspectionJobId,
  prefillSnapshot,
  formValues,
  hvacSystems,
  plumbingSystems,
  dirty,
  setFormValues,
  setHvacSystems,
  setPlumbingSystems,
  setDirty,
  setRestoredFromDraft,
  setDraftCheckComplete,
  isPending,
}: UseInspectionReportDraftParams) {
  // stateRef mirrors the latest state values so the pagehide/beforeunload handlers
  // always flush current data — event listeners capture the ref, not the values.
  const stateRef = useRef({
    formValues,
    hvacSystems,
    plumbingSystems,
    dirty,
    homeInspectionJobId,
  });
  const isPendingRef = useRef(false);

  useEffect(() => {
    isPendingRef.current = isPending;
  }, [isPending]);

  useEffect(() => {
    stateRef.current = { formValues, hvacSystems, plumbingSystems, dirty, homeInspectionJobId };
  }, [formValues, hvacSystems, plumbingSystems, dirty, homeInspectionJobId]);

  // Init effect: runs once per jobId change. Checks localStorage and either restores
  // or applies prefill data. Always calls setDraftCheckComplete(true) at the end so
  // useMediaDraft knows it's safe to act on hasDraft.
  useEffect(() => {
    const prefill = parsePrefill(prefillSnapshot);
    const hasPrefill = Object.keys(prefill).length > 0;

    if (!homeInspectionJobId) {
      setRestoredFromDraft(false);
      setDraftCheckComplete(true);
      if (hasPrefill) {
        setFormValues((prev) => ({ ...prev, ...prefill }));
      }
      return;
    }

    const draft = loadDraft(homeInspectionJobId);
    if (draft) {
      setFormValues({ ...initialFormValues, ...(draft.formValues as Partial<InspectionFormValues>) });
      // UUID backfill: drafts saved before system UUIDs were introduced have no id field.
      // Spread a freshly created default (which has a UUID) then overwrite with the saved data.
      const hvac =
        draft.hvacSystems.length > 0
          ? draft.hvacSystems.map((s) => (s.id ? s : { ...createDefaultHvacSystem(), ...s }))
          : [createDefaultHvacSystem()];
      const plumbing =
        draft.plumbingSystems.length > 0
          ? draft.plumbingSystems.map((s) => (s.id ? s : { ...createDefaultPlumbingSystem(), ...s }))
          : [createDefaultPlumbingSystem()];
      setHvacSystems(hvac);
      setPlumbingSystems(plumbing);
      setRestoredFromDraft(true);
      // dirty=true re-arms the debounced save loop so subsequent edits keep the draft current.
      setDirty(true);
    } else {
      setRestoredFromDraft(false);
      if (hasPrefill) {
        setFormValues((prev) => ({ ...prev, ...prefill }));
      }
    }
    // Must always be called — tells useMediaDraft that hasDraft is now settled.
    setDraftCheckComplete(true);
  }, [
    homeInspectionJobId,
    prefillSnapshot,
    setFormValues,
    setHvacSystems,
    setPlumbingSystems,
    setDirty,
    setRestoredFromDraft,
    setDraftCheckComplete,
  ]);

  // Debounced persist: only runs when dirty=true, so a pristine form never writes storage.
  // Resets on every state change — the draft is written 400ms after the last edit.
  useEffect(() => {
    if (!homeInspectionJobId || !dirty) return;
    const timer = window.setTimeout(() => {
      saveDraft(homeInspectionJobId, {
        formValues: { ...formValues } as Record<string, string | boolean>,
        hvacSystems,
        plumbingSystems,
        savedAt: new Date().toISOString(),
      });
    }, PERSIST_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [formValues, hvacSystems, plumbingSystems, homeInspectionJobId, dirty]);

  // Synchronous flush on tab close / navigation. Runs once on mount.
  // Uses stateRef so it always has the latest values without re-registering the listeners.
  useEffect(() => {
    const flushDraft = () => {
      const s = stateRef.current;
      if (s.homeInspectionJobId && s.dirty) {
        saveDraft(s.homeInspectionJobId, {
          formValues: { ...s.formValues } as Record<string, string | boolean>,
          hvacSystems: s.hvacSystems,
          plumbingSystems: s.plumbingSystems,
          savedAt: new Date().toISOString(),
        });
      }
    };
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      flushDraft();
      const s = stateRef.current;
      if (s.dirty && !isPendingRef.current) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('pagehide', flushDraft);
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('pagehide', flushDraft);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);
}
